import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { Button } from "@mui/material";
import { gtag } from 'ga-gtag';
import axios from 'axios';
import { FaArrowRight } from 'react-icons/fa';
import paths from "../../../paths";
import SnackbarAlert from "../../common/SnackbarAlert";
import { useSearchParams } from "react-router-dom";
import { useApp } from '../../../context/AppContext';
import "./SalesPageNew.css";
const S3_BASE = 'https://openaccessimages.s3.eu-west-2.amazonaws.com/sales-page-assets/images';
const HeroImage = `${S3_BASE}/hero-features-william.webp`;
const AgentDesignerImg = `${S3_BASE}/agent-designer.jpg`;
const AgentWriterImg = `${S3_BASE}/agent-writer.jpg`;
const AgentGreeterImg = `${S3_BASE}/agent-greeter.jpg`;

const PRICING = {
   GBP: { symbol: "£", monthly: "25" },
   USD: { symbol: "$", monthly: "25" },
   EUR: { symbol: "€", monthly: "25" },
   SEK: { symbol: " kr", monthly: "333", suffix: true },
   AUD: { symbol: "AU$", monthly: "46" },
   CAD: { symbol: "CA$", monthly: "46" },
   NZD: { symbol: "NZ$", monthly: "54" },
};

const getAgentStates = (t) => [
   { avatar: AgentDesignerImg, name: t('salesPageNew.hero.agents.designName'), job: t('salesPageNew.hero.agents.designJob'), status: t('salesPageNew.hero.agents.designStatus'), dotColor: "#10b981" },
   { avatar: AgentWriterImg, name: t('salesPageNew.hero.agents.copywritingName'), job: t('salesPageNew.hero.agents.copywritingJob'), status: t('salesPageNew.hero.agents.copywritingStatus'), dotColor: "#3b82f6" },
   { avatar: AgentGreeterImg, name: t('salesPageNew.hero.agents.salesName'), job: t('salesPageNew.hero.agents.salesJob'), status: t('salesPageNew.hero.agents.salesStatus'), dotColor: "#10b981" },
];

function HeroDashedLines() {
   return (
      <svg
         aria-hidden="true"
         style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}
         viewBox="0 0 960 760"
         preserveAspectRatio="xMidYMid meet"
         fill="none"
      >
         <path d="M 430 -20 L 120 -20 Q 100 -20 100 0 L 100 680 Q 100 700 120 700 L 430 700" className="sp-hero-dash" />
         <path d="M 530 -20 L 840 -20 Q 860 -20 860 0 L 860 680 Q 860 700 840 700 L 530 700" className="sp-hero-dash" />
      </svg>
   );
}

function AgentNotification({ isMobile, t }) {
   const agentStates = getAgentStates(t);
   return (
      <div
         style={{
            position: 'absolute',
            left: isMobile ? '50%' : '68%',
            transform: 'translateX(-50%)',
            top: isMobile ? 'auto' : '10%',
            bottom: isMobile ? '-60px' : 'auto',
            width: isMobile ? '90%' : '50%',
            maxWidth: isMobile ? 260 : 300,
            zIndex: 30,
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 6 : 8,
            animation: 'sp-agent-float 6s ease-in-out infinite',
         }}
      >
         {agentStates.map((s, idx) => (
            <div
               key={s.name}
               style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? 6 : 8,
                  borderRadius: isMobile ? 12 : 16,
                  background: 'rgba(255,255,255,0.5)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 10px 30px -10px rgba(15,23,42,0.15)',
                  border: '1px solid #f3f4f6',
                  padding: isMobile ? '8px 10px' : '10px 12px',
                  opacity: 0,
                  animation: 'sp-agent-loop 8s ease-in-out infinite',
                  animationDelay: `${idx * 300}ms`,
               }}
            >
               <img src={s.avatar} alt={s.name} style={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
               <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 600, color: 'var(--sp-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'none' }}>{s.name}</div>
                  <div style={{ fontSize: isMobile ? 10 : 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textTransform: 'none' }}>{s.job}</div>
               </div>
               <div style={{
                  display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 6, borderRadius: 9999, background: '#f9fafb',
                  border: '1px solid #f3f4f6', padding: isMobile ? '3px 6px' : '4px 8px',
               }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dotColor }} />
                  <span style={{ fontSize: isMobile ? 9 : 10, fontWeight: 500, color: '#374151', whiteSpace: 'nowrap' }}>{s.status}</span>
               </div>
            </div>
         ))}
      </div>
   );
}

function HeroTypingInput({ inputRef, onSubmit }) {
   const { t } = useTranslation();
   const [value, setValue] = useState('');
   const [typedPlaceholder, setTypedPlaceholder] = useState('');
   const [focused, setFocused] = useState(false);
   const fullPlaceholder = t('salesPageNew.hero.socialPlaceholder', "yourhandle \u2014 we'll build the rest");

   // Typewriter effect for placeholder
   useEffect(() => {
      if (focused || value) return;
      let i = 0;
      setTypedPlaceholder('');
      const timer = setInterval(() => {
         i++;
         if (i <= fullPlaceholder.length) {
            setTypedPlaceholder(fullPlaceholder.slice(0, i));
         } else {
            clearInterval(timer);
         }
      }, 50);
      return () => clearInterval(timer);
   }, [focused, value, fullPlaceholder]);

   const handleChange = (e) => {
      setValue(e.target.value);
      localStorage.setItem('withme_social_link', e.target.value);
   };

   const showTypewriter = !value && !focused;

   return (
      <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, width: '100%', position: 'relative' }}>
         <span style={{ fontSize: 15, fontWeight: 500, color: '#6B7280', padding: '0 0 0 14px', flexShrink: 0 }}>@</span>
         {showTypewriter && (
            <div style={{
               position: 'absolute', left: 34, top: 0, bottom: 0, display: 'flex', alignItems: 'center',
               fontSize: 14, color: '#9CA3AF', pointerEvents: 'none', fontFamily: 'inherit',
            }}>
               {typedPlaceholder}
               <span style={{ marginLeft: 1, display: 'inline-block', width: 1, height: '1.1em', backgroundColor: '#9CA3AF', verticalAlign: 'middle', animation: 'heroTypingCursorBlink 1s ease-in-out infinite' }} />
            </div>
         )}
         <input
            ref={inputRef}
            id="hero-social-input"
            type="text"
            value={value}
            onChange={handleChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (onSubmit) onSubmit(); } }}
            placeholder={focused ? fullPlaceholder : ''}
            style={{
               minWidth: 0, flex: 1, padding: '0 6px', textAlign: 'left', fontSize: 14,
               color: 'var(--sp-ink)', border: 'none', outline: 'none', background: 'transparent',
               width: '100%', fontFamily: 'inherit',
            }}
         />
      </div>
   );
}

export default function Hero(props) {
   const { t } = useTranslation();
   const [searchParams] = useSearchParams();
   const { currency } = useApp();
   const socialInputRef = useRef(null);

   const price = PRICING[currency] || PRICING.USD;
   const formattedPrice = price.suffix ? `${price.monthly}${price.symbol}` : `${price.symbol}${price.monthly}`;

   const getRegisterUrl = () => {
      let direct_url = '';
      const referralId = searchParams.get("id") || props.referralId;
      const passiveReferralId = searchParams.get("passive_id") || props.passiveReferralId;
      if (referralId != null && referralId !== '') {
         direct_url = '/become-a-creator/quick-signup?id=' + referralId;
      } else if (passiveReferralId != null && passiveReferralId !== '') {
         direct_url = '/become-a-creator/quick-signup?passive_id=' + passiveReferralId;
      } else {
         direct_url = '/become-a-creator/quick-signup';
      }
      // Append UTM params
      const utmParams = { utm_source: props.utmSource, utm_medium: props.utmMedium, utm_campaign: props.utmCampaign, utm_content: props.utmContent, utm_term: props.utmTerm };
      Object.entries(utmParams).forEach(([key, value]) => {
         if (value != null && value !== '') {
            direct_url += (direct_url.includes('?') ? '&' : '?') + key + '=' + value;
         }
      });
      // Append fbclid
      const _fbclid = new URLSearchParams(window.location.search).get('fbclid') || localStorage.getItem('fbclid');
      if (_fbclid) {
         direct_url += (direct_url.includes('?') ? '&' : '?') + 'fbclid=' + encodeURIComponent(_fbclid);
      }
      return direct_url;
   };

   const _appendFbclid = (url) => {
      const fb = new URLSearchParams(window.location.search).get('fbclid') || localStorage.getItem('fbclid');
      if (fb) return url + (url.includes('?') ? '&' : '?') + 'fbclid=' + encodeURIComponent(fb);
      return url;
   };

   const handleCTA = () => {
      if (props.logged_in === true) {
         if (props.pre_influencer === true && props.influencer_bool !== true) {
            window.location.assign(_appendFbclid('/become-a-creator/start-free-trial'));
         } else if (props.pre_influencer === false && props.influencer_bool !== true) {
            window.location.assign(_appendFbclid('/become-a-creator/quick-signup'));
         } else {
            window.location.assign('/' + props.current_user);
         }
      } else {
         window.location.assign(getRegisterUrl());
      }
   };

   const getButtonText = () => {
      if (props.logged_in === true) {
         if (props.pre_influencer === true && props.influencer_bool === true) return t('marketing.hero.myHub');
         if (props.pre_influencer === true || props.influencer_bool === false) return t('marketing.hero.continueForFree');
         return t('marketing.hero.myHub');
      }
      return t('marketing.hero.start14DaysFree');
   };

   return (
      <section style={{ position: 'relative', overflow: 'hidden', paddingBottom: 48, paddingTop: 100 }}>
         {/* Text content */}
         <div style={{ position: 'relative', maxWidth: 960, margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
            {/* "New 2026" pill */}
            <button
               type="button"
               onClick={() => {
                  const el = document.getElementById('agents-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
               }}
               style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 9999,
                  border: '1px solid rgba(59,85,243,0.25)', background: 'rgba(255,255,255,0.7)',
                  padding: '6px 14px', fontSize: 12, fontWeight: 500, color: 'var(--sp-ink)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)', backdropFilter: 'blur(4px)',
                  cursor: 'pointer', marginBottom: 24, transition: 'border-color 0.2s, background 0.2s',
               }}
            >
               <span style={{
                  display: 'inline-flex', alignItems: 'center', borderRadius: 9999,
                  background: 'rgba(59,85,243,0.1)', padding: '2px 8px', fontSize: 10,
                  fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--sp-brand)',
               }}>
                  {t('salesPageNew.hero.badgeLabel')}
               </span>
               <span>{t('salesPageNew.hero.badgeText')}</span>
               <FaArrowRight style={{ width: 12, height: 12 }} />
            </button>

            {/* Main headline */}
            <h1 style={{
               fontSize: props.windowWidth < 380 ? 36 : (props.windowWidth < 768 ? 48 : 72),
               lineHeight: 1.05, letterSpacing: '-0.02em', color: 'var(--sp-ink)',
               fontWeight: 600, textTransform: 'uppercase', margin: 0, overflowWrap: 'break-word',
            }}>
               {t('salesPageNew.hero.heading')}
               <br />
               <span className="sp-accent-italic" style={{
                  textTransform: 'none',
                  fontSize: props.windowWidth < 380 ? 24 : (props.windowWidth < 768 ? 30 : (props.windowWidth < 1024 ? 40 : 48)),
               }}>
                  {t('salesPageNew.hero.subheading')}
               </span>
            </h1>

            {/* Subtitle */}
            <p style={{ margin: '24px auto 0', maxWidth: 512, fontSize: 18, color: 'var(--sp-text-soft)' }}>
               {t('salesPageNew.hero.priceSubtitle', { price: formattedPrice })}
            </p>

            {/* Search bar CTA */}
            <div style={{
               marginTop: 40, maxWidth: 672, marginLeft: 'auto', marginRight: 'auto',
               display: 'flex', flexDirection: props.windowWidth < 640 ? 'column' : 'row',
               alignItems: 'center', gap: props.windowWidth < 640 ? 12 : 8,
               borderRadius: 9999,
               border: props.windowWidth < 640 ? 'none' : '1px solid rgba(59,85,243,0.3)',
               background: props.windowWidth < 640 ? 'transparent' : '#fff',
               padding: props.windowWidth < 640 ? 0 : 8,
               boxShadow: props.windowWidth < 640 ? 'none' : '0 20px 60px -20px rgba(59,85,243,0.35)',
            }}>
               <div style={{
                  display: 'flex', flex: 1, alignItems: 'center', gap: 8, width: '100%',
                  borderRadius: 9999,
                  border: props.windowWidth < 640 ? '1px solid rgba(59,85,243,0.3)' : 'none',
                  background: props.windowWidth < 640 ? '#fff' : 'transparent',
                  padding: props.windowWidth < 640 ? 8 : 0,
                  boxShadow: props.windowWidth < 640 ? '0 20px 60px -20px rgba(59,85,243,0.35)' : 'none',
               }}>
                  <HeroTypingInput inputRef={socialInputRef} onSubmit={handleCTA} />
               </div>
               <button
                  className="sp-btn-primary"
                  onClick={handleCTA}
                  style={{
                     padding: '14px 24px', fontSize: 15, flexShrink: 0, whiteSpace: 'nowrap',
                     width: props.windowWidth < 640 ? '100%' : 'auto',
                     fontWeight: 600,
                  }}
               >
                  {t('salesPageNew.hero.startBuildingFree', 'Start building free')} <FaArrowRight style={{ width: 14, height: 14, marginLeft: 4 }} />
               </button>
            </div>

            {/* Trust badges */}
            <div style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: props.windowWidth < 640 ? '8px 16px' : '8px 28px' }}>
               {[
                  t('salesPageNew.hero.free14DayTrial', 'Free 14-day trial'),
                  t('salesPageNew.hero.noCreditCard', 'No credit card required'),
                  t('salesPageNew.hero.zeroCommission', '0% commission'),
               ].map((text, i) => (
                  <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6B7280' }}>
                     <span style={{ color: '#10b981', fontSize: 15 }}>✓</span> {text}
                  </span>
               ))}
            </div>

            {/* Trust line */}
            <p style={{ marginTop: 10, fontSize: 14, color: 'var(--sp-text-muted)' }}>
               {t('salesPageNew.hero.trustedBy')} <span style={{ fontWeight: 600, color: 'var(--sp-ink)' }}>{t('salesPageNew.hero.trustedCount')}</span> {t('salesPageNew.hero.creatorEntrepreneurs')}
            </p>
         </div>

         {/* Phone mockup with dashed lines */}
         <div style={{ margin: '48px auto 0', display: 'flex', justifyContent: 'center' }}>
            <div style={{ position: 'relative', width: '80%', maxWidth: 825 }}>
               <HeroDashedLines />
               <img
                  src={HeroImage}
                  alt="Your withme.so hub on a phone"
                  style={{ position: 'relative', zIndex: 10, width: '100%', height: 'auto' }}
               />
               <AgentNotification isMobile={props.windowWidth < 768} t={t} />
            </div>
         </div>

         {/* Snackbar for email validation */}
         <SnackbarAlert
            msg={props.emailSubmit?.message}
            backgroundColor={'#3B55F3'}
            open={props.emailSubmit?.open}
            severity={props.emailSubmit?.severity}
            handleClose={() => {
               if (props.setEmailSubmit) {
                  props.setEmailSubmit({
                     open: false,
                     severity: props.submitError ? 'error' : 'success',
                     message: ''
                  });
               }
            }}
         />
      </section>
   );
}
